import { isWeb } from "@memorang/helpers/platform";
import Box from "@memorang/ui/components/Box";
import {
	type SessionItemsNavFilterValue,
	SessionItemsNavPane,
} from "@memorang/ui/components/SessionItemsNavPane";
import { useDevice } from "@memorang/ui/useDevice";
import { useLocalSearchParams } from "expo-router";
import { type ReactNode, createContext, useContext, useState } from "react";
import { useSessionItemsNavCore } from "../hooks/useSessionItemsNavCore";

type Core = ReturnType<typeof useSessionItemsNavCore>;

type SessionItemsNavLayoutValue = Core & {
	showSessionItemsNav: boolean;
	setShowSessionItemsNav: React.Dispatch<React.SetStateAction<boolean>>;
	sessionItemsNavSelectedFilters: SessionItemsNavFilterValue[];
	setSessionItemsNavSelectedFilters: React.Dispatch<
		React.SetStateAction<SessionItemsNavFilterValue[]>
	>;
};

const SessionItemsNavLayoutContext =
	createContext<SessionItemsNavLayoutValue | null>(null);

export const useSessionItemsNavLayout = () =>
	useContext(SessionItemsNavLayoutContext);

type Props = { children: ReactNode };

export const SessionItemsNavLayoutProvider = ({ children }: Props) => {
	const { isLargeWindowSize } = useDevice();
	const { index } = useLocalSearchParams<{ index: string }>();
	const activeItemIndex = index ? Number.parseInt(index) - 1 : 0;

	const [showSessionItemsNav, setShowSessionItemsNav] = useState(false);
	const [sessionItemsNavSelectedFilters, setSessionItemsNavSelectedFilters] =
		useState<SessionItemsNavFilterValue[]>([]);

	const core = useSessionItemsNavCore({
		setShowSessionItemsNav,
		sessionItemsNavSelectedFilters,
		setSessionItemsNavSelectedFilters,
	});

	const value: SessionItemsNavLayoutValue = {
		...core,
		showSessionItemsNav,
		setShowSessionItemsNav,
		sessionItemsNavSelectedFilters,
		setSessionItemsNavSelectedFilters,
	};

	const showDesktopNav =
		isLargeWindowSize && core.isSummativeTest && showSessionItemsNav;

	return (
		<SessionItemsNavLayoutContext.Provider value={value}>
			<Box flex={1} flexDirection="row" alignItems="stretch" minWidth={0}>
				{showDesktopNav && (
					<Box
						alignSelf="stretch"
						style={{
							flexShrink: 0,
							minHeight: 0,
							...(isWeb ? { height: "100%" } : {}),
						}}
					>
						<SessionItemsNavPane
							presentation="inline"
							visible={showSessionItemsNav}
							onClose={core.handleCloseSessionItemsNav}
							items={core.sessionItemsNavItems}
							activeItemIndex={activeItemIndex}
							filters={core.sessionItemsNavFilters}
							selectedFilters={sessionItemsNavSelectedFilters}
							onSelectedFiltersChange={setSessionItemsNavSelectedFilters}
							onItemPress={core.handleSessionItemsNavPress}
						/>
					</Box>
				)}
				<Box flex={1} minWidth={0}>
					{children}
				</Box>
			</Box>
		</SessionItemsNavLayoutContext.Provider>
	);
};
